/* eslint-disable prettier/prettier */
import { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { confirmDialog } from "primereact/confirmdialog";
import AdminServices from "services/admin-services";
import { setToaster } from "common/toastStore";
import { Sidebar } from "primereact/sidebar";
import AddDocumentTypes from "./addDocumentType";
import { handlingUrlParams } from "Utils/AppUtils";
import Loader from "common/Loader";

interface PackageDocTypesProps {
  selectedRow: any;
  packageData: any;
}

const PackageDocTypes = ({
  selectedRow,
  packageData,
}: PackageDocTypesProps) => {
  const [data, setData] = useState<any[]>([]);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [rowLoadingId, setRowLoadingId] = useState<number | null>(null);
  const [showAddDocTypes, setAddDocTypes] = useState<boolean>(false);
  const [originalCache, setOriginalCache] = useState<Record<number, any>>({});
  const [loader, setLoader] = useState<boolean>(false);

  const normalizeName = (value: string) => value.trim().toLowerCase();

  useEffect(() => {
    if (!selectedRow) return;

    let sorted = [...selectedRow].sort(
      (a, b) => a.processOrder - b.processOrder,
    );

    setData(sorted);
  }, [selectedRow]);

  /* ---------- Edit ---------- */

  const handleEdit = (row: any) => {
    setOriginalCache((prev) => ({
      ...prev,
      [row.id]: { ...row },
    }));
    setEditingRowId(row.id);
  };

  const handleCancel = (row: any) => {
    const original = originalCache[row.id];
    if (original) {
      setData((prev) =>
        prev.map((item) => (item.id === row.id ? original : item)),
      );
    }
    setEditingRowId(null);
  };

  const handleSave = async (row: any) => {
    const duplicateExists = data.some(
      (item) =>
        item.id !== row.id &&
        normalizeName(item?.name || "") === normalizeName(row?.name || ""),
    );

    if (duplicateExists) {
      setToaster({
        severity: "warn",
        summary: "Duplicate Document Type",
        detail: "Document type already exists.",
      });
      return;
    }

    if (!row.processOrder) return;

    try {
      setRowLoadingId(row.id);

      await AdminServices.updateInvestorDocPackages(row);

      setEditingRowId(null);
      setToaster({
        severity: "success",
        summary: "Success",
        detail: "Document type updated successfully",
      });
    } catch (error) {
      handleCancel(row);
      setToaster({
        severity: "error",
        summary: "Error",
        detail: "Failed to update document type",
      });
    } finally {
      setRowLoadingId(null);
    }
  };

  const handleToggleActive = async (row: any) => {
    const updatedRow = {
      ...row,
      isActive: false,
    };

    try {
      setRowLoadingId(row.id);

      await AdminServices.updateInvestorDocPackages(updatedRow);

      setData((prev) =>
        prev.map((item) => (item.id === row.id ? updatedRow : item)),
      );
      getPackageDocTypesByID(packageData?.id);
      setToaster({
        severity: "success",
        summary: "Success",
        detail: `Document type deleted successfully`,
      });
    } catch (error) {
      setToaster({
        severity: "error",
        summary: "Error",
        detail: "Failed to update status",
      });
    } finally {
      setRowLoadingId(null);
    }
  };

  /* ---------- Templates ---------- */

  const processOrderTemplate = (row: any) => {
    if (editingRowId !== row.id) return row.processOrder;

    return (
      <InputNumber
        value={row.processOrder}
        onValueChange={(e) =>
          setData((prev) =>
            prev.map((item) =>
              item.id === row.id ? { ...item, processOrder: e.value } : item,
            ),
          )
        }
        className="w-6rem"
      />
    );
  };

  const nameTemplate = (row: any) => {
    if (editingRowId !== row.id) return row.name;

    return (
      <InputText
        value={row.name || ""}
        onChange={(e) =>
          setData((prev) =>
            prev.map((item) =>
              item.id === row.id ? { ...item, name: e.target.value } : item,
            ),
          )
        }
        className="w-full"
      />
    );
  };

  const requiredTemplate = (row: any) => {
    if (editingRowId !== row.id) return row.required ? "Yes" : "No";

    return (
      <Checkbox
        checked={row.required}
        onChange={(e) =>
          setData((prev) =>
            prev.map((item) =>
              item.id === row.id ? { ...item, required: e.checked } : item,
            ),
          )
        }
      />
    );
  };

  const actionTemplate = (row: any) => {
    const isEditing = editingRowId === row.id;
    const isLoading = rowLoadingId === row.id;

    if (isLoading) return <i className="pi pi-spin pi-spinner" />;

    return (
      <div className="flex gap-2">
        {!isEditing ? (
          <>
            <Button
              icon="pi pi-pencil"
              className="p-button-sm p-button-outlined"
              onClick={() => handleEdit(row)}
            />
            <Button
              icon="pi pi-trash"
              className="p-button-sm p-button-outlined p-button-danger"
              onClick={() =>
                confirmDialog({
                  message: `Are you sure you want to delete document type "${row?.name || ""}"?`,
                  header: "Delete Confirmation",
                  icon: "pi pi-exclamation-triangle",
                  acceptClassName: "p-button-danger",
                  accept: () => handleToggleActive(row),
                })
              }
            />
          </>
        ) : (
          <>
            <Button
              icon="pi pi-check"
              className="p-button-sm p-button-outlined p-button-success"
              onClick={() => handleSave(row)}
            />
            <Button
              icon="pi pi-times"
              className="p-button-sm p-button-outlined p-button-danger"
              onClick={() => handleCancel(row)}
            />
          </>
        )}
      </div>
    );
  };

  const getPackageDocTypesByID = (id: any) => {
    setLoader(true);
    AdminServices.getPackageDocTypesByID(
      handlingUrlParams({
        PackageId: id,
      }),
    )
      .then((response: any) => {
        setData(response?.data);
        setLoader(false);
      })
      .catch((e: Error) => {
        console.error(e);
        setLoader(false);
      });
  };

  return (
    <div className="p-4">
      <div className="flex justify-between mb-4 items-center">
        <h2 className="text-xl font-bold">
          Document Types - {packageData?.name}
        </h2>
        <Button
          onClick={() => setAddDocTypes(true)}
          label="Add Document Type"
          icon="pi pi-plus"
          className="p-button-sm p-button-outlined p-button-success"
        />
      </div>

      <DataTable
        className="p-datatable-sm"
        value={data}
        responsiveLayout="scroll"
        rows={10}
        paginator
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
      >
        <Column field="name" header="Document Type" body={nameTemplate} />

        <Column header="Process Order" body={processOrderTemplate} />

        <Column header="Required" body={requiredTemplate} />

        <Column header="Actions" body={actionTemplate} />
      </DataTable>
      <Sidebar
        visible={showAddDocTypes}
        position="right"
        onHide={() => setAddDocTypes(false)}
        style={{ width: 520 }}
      >
        <AddDocumentTypes
          onHide={() => setAddDocTypes(false)}
          selectedRow={selectedRow}
          packageData={packageData}
          onSuccess={() => getPackageDocTypesByID(packageData?.id)}
        />
      </Sidebar>
      {loader && <Loader />}
    </div>
  );
};

export default PackageDocTypes;
